import { NavLink, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Logo } from './Logo';
import { EditLocaleProvider } from './EditLocale';

/** Stroked 1.5px icons at 24×24, so they sit evenly against 14px nav text. */
const icons: Record<string, ReactNode> = {
  brands: (
    <path d="M3 9.5 5 5a2 2 0 0 1 1.8-1h10.4A2 2 0 0 1 19 5l2 4.5M3 9.5h18M3 9.5V17a1 1 0 0 0 1 1h1.5a1 1 0 0 0 1-1v-1.5m14-6V17a1 1 0 0 1-1 1H17a1 1 0 0 1-1-1v-1.5m-9.5 0h11M7 13h.01M17 13h.01" />
  ),
  publish: <path d="M12 19V5m0 0-6 6m6-6 6 6M5 21h14" />,
  users: (
    <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm10 8v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.5 4.6a3.5 3.5 0 0 1 0 6.8" />
  ),
};

function NavIcon({ name }: { name: keyof typeof icons }) {
  return (
    <svg
      className="nav-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icons[name]}
    </svg>
  );
}

function initials(name: string): string {
  return (
    name
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

export function Layout() {
  const { user, signOut } = useAuth();

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="logo-plate">
            <Logo className="sidebar-logo" />
          </span>
          <span className="sidebar-sub">Brands CMS</span>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/brands">
            <NavIcon name="brands" />
            Brands
          </NavLink>
          <NavLink to="/publish">
            <NavIcon name="publish" />
            Publish
          </NavLink>
          {user?.isAdmin ? (
            <NavLink to="/users">
              <NavIcon name="users" />
              Users
            </NavLink>
          ) : null}
        </nav>

        <div className="sidebar-foot">
          <div className="sidebar-user">
            <span className="avatar" aria-hidden="true">
              {initials(user?.name ?? '')}
            </span>
            <span className="sidebar-user-text">
              <span className="sidebar-user-name" title={user?.email}>
                {user?.name}
              </span>
              <span className="sidebar-user-role">{user?.isAdmin ? 'Admin' : 'Editor'}</span>
            </span>
          </div>
          <button type="button" className="btn signout" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="content">
        {/* One editing language for the whole app, so it survives navigation
            between a brand, its models and their variants. */}
        <EditLocaleProvider>
          <Outlet />
        </EditLocaleProvider>
      </main>
    </div>
  );
}

/**
 * Standard page heading: title, optional slug chip, then actions pushed right.
 * Sticky so the primary action stays reachable on the long model form.
 */
export function PageHeader({
  title,
  slug,
  children,
}: {
  title: string;
  slug?: string;
  children?: ReactNode;
}) {
  return (
    <header className="page-header">
      <h1>{title}</h1>
      {slug ? <code className="slug">{slug}</code> : null}
      {children ? <div className="page-actions">{children}</div> : null}
    </header>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <p className="loading" role="status">
      {label}
    </p>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <p className="error-note" role="alert">
      {message}
    </p>
  );
}

export function Breadcrumbs({ trail }: { trail: Array<{ label: string; to?: string }> }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      {trail.map((crumb, i) => (
        <span key={`${crumb.label}-${i}`}>
          {crumb.to ? <NavLink to={crumb.to}>{crumb.label}</NavLink> : <span>{crumb.label}</span>}
          {i < trail.length - 1 ? <span className="sep">/</span> : null}
        </span>
      ))}
    </nav>
  );
}
